import React, { useState } from 'react';
import './Signup.css';
import { API_BASE_URL } from '../../utils/api';

function Signup({ onSignup, onSwitchToLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!email || !password || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          password, 
          firstName, 
          lastName 
        }),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('username', data.username);
        localStorage.setItem('email', data.email);
        onSignup();
      } else {
        setError(data.error || 'Signup failed');
      }
    } catch (err) {
      if (err.message && err.message.includes('fetch')) {
        setError('Failed to connect to server. Please ensure the backend is running on port 5000.');
      } else {
        setError(err.message || 'Failed to connect to server');
      }
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    // Debug: Log the client ID (remove in production)
    console.log('Google Client ID from env:', clientId ? 'Found' : 'Not found', clientId ? `(${clientId.substring(0, 20)}...)` : '');
    
    // Check if client ID is missing, empty, or placeholder
    if (!clientId || clientId.trim() === '' || clientId === 'YOUR_GOOGLE_CLIENT_ID' || clientId.includes('your-google-client-id')) {
      setError('Google login is not configured. Please add your Google Client ID to the .env file and restart the server. See GOOGLE_LOGIN_SETUP.md for instructions.');
      console.error('Google Client ID not found. Current value:', clientId);
      return;
    }

    try {
      // Load Google Identity Services
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        
        script.onload = () => {
          initializeGoogleSignIn(clientId);
        };
        
        script.onerror = () => {
          setError('Failed to load Google login service');
        };
      } else {
        initializeGoogleSignIn(clientId);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setError('Failed to initialize Google login. Please try again.');
    }
  };

  const initializeGoogleSignIn = (clientId) => {
    try {
      if (!window.google || !window.google.accounts) {
        setError('Google login service not available');
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCallback,
      });

      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Fallback to popup
          try {
            const tokenClient = window.google.accounts.oauth2.initTokenClient({
              client_id: clientId,
              scope: 'email profile',
              callback: handleGoogleTokenResponse,
            });
            tokenClient.requestAccessToken();
          } catch (err) {
            console.error('Token client error:', err);
            setError('Failed to start Google login. Please try again.');
          }
        }
      });
    } catch (error) {
      console.error('Google sign-in initialization error:', error);
      setError('Failed to initialize Google login');
    }
  };

  const handleGoogleCallback = async (response) => {
    try {
      // Google's token goes to the server whole, signature included - see the
      // same change in Login. Reading the payload here and posting the email
      // out of it meant the server was trusting the browser's word for who
      // this was.
      const apiResponse = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential: response.credential }),
      });

      const data = await apiResponse.json();

      if (data.success) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('username', data.username);
        localStorage.setItem('email', data.email);
        localStorage.setItem('role', data.role || 'member');
        if (data.avatarUrl) {
          localStorage.setItem('avatarUrl', data.avatarUrl);
        }
        onSignup();
      } else {
        setError(data.error || 'Google login failed');
      }
    } catch (error) {
      console.error('Google callback error:', error);
      setError('Failed to process Google login');
    }
  };

  const handleGoogleTokenResponse = async (tokenResponse) => {
    try {
      // The popup path carries an access token rather than a signed ID token.
      // The server asks Google who it belongs to and checks it was issued for
      // this app, instead of us reading the profile here and passing it on.
      const apiResponse = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken: tokenResponse.access_token }),
      });

      const data = await apiResponse.json();

      if (data.success) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('username', data.username);
        localStorage.setItem('email', data.email);
        localStorage.setItem('role', data.role || 'member');
        if (data.avatarUrl) {
          localStorage.setItem('avatarUrl', data.avatarUrl);
        }
        onSignup();
      } else {
        setError(data.error || 'Google login failed');
      }
    } catch (error) {
      console.error('Google token response error:', error);
      setError('Failed to process Google login');
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="login-content">
          <div className="login-header">
            <div className="logo">
              <span className="logo-text">TF</span>
            </div>
            <h1 className="login-title">Create your account</h1>
            <p className="login-subtitle">Sign up to get started with TestFlow</p>
          </div>
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <div className="input-wrapper">
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <div className="input-wrapper">
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.5 6.66667L10 11.6667L17.5 6.66667M3.33333 15H16.6667C17.5871 15 18.3333 14.2538 18.3333 13.3333V6.66667C18.3333 5.74619 17.5871 5 16.6667 5H3.33333C2.41286 5 1.66667 5.74619 1.66667 6.66667V13.3333C1.66667 14.2538 2.41286 15 3.33333 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.8333 9.16667H4.16667C3.24619 9.16667 2.5 9.91286 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91286 16.7538 9.16667 15.8333 9.16667Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.83333 9.16667V5.83333C5.83333 4.72826 6.27232 3.66846 7.05372 2.88706C7.83512 2.10565 8.89493 1.66667 10 1.66667C11.1051 1.66667 12.1649 2.10565 12.9463 2.88706C13.7277 3.66846 14.1667 4.72826 14.1667 5.83333V9.16667" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {showPassword ? (
                      <path d="M2.5 2.5L17.5 17.5M8.33333 8.33333C7.89131 8.77535 7.66667 9.39565 7.66667 10C7.66667 10.6043 7.89131 11.2247 8.33333 11.6667M8.33333 8.33333L11.6667 11.6667M8.33333 8.33333L5.83333 5.83333M11.6667 11.6667C12.1087 11.2247 12.3333 10.6043 12.3333 10C12.3333 9.39565 12.1087 8.77535 11.6667 8.33333M11.6667 11.6667L14.1667 14.1667M5.83333 5.83333C4.25 7.41667 2.5 9.58333 2.5 10C2.5 10.4167 4.25 12.5833 5.83333 14.1667M5.83333 5.83333L3.33333 3.33333M14.1667 14.1667C15.75 12.5833 17.5 10.4167 17.5 10C17.5 9.58333 15.75 7.41667 14.1667 5.83333M14.1667 14.1667L16.6667 16.6667" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <path d="M2.5 10C2.5 10 5.83333 5.83333 10 5.83333C14.1667 5.83333 17.5 10 17.5 10C17.5 10 14.1667 14.1667 10 14.1667C5.83333 14.1667 2.5 10 2.5 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.8333 9.16667H4.16667C3.24619 9.16667 2.5 9.91286 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91286 16.7538 9.16667 15.8333 9.16667Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.83333 9.16667V5.83333C5.83333 4.72826 6.27232 3.66846 7.05372 2.88706C7.83512 2.10565 8.89493 1.66667 10 1.66667C11.1051 1.66667 12.1649 2.10565 12.9463 2.88706C13.7277 3.66846 14.1667 4.72826 14.1667 5.83333V9.16667" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {showConfirmPassword ? (
                      <path d="M2.5 2.5L17.5 17.5M8.33333 8.33333C7.89131 8.77535 7.66667 9.39565 7.66667 10C7.66667 10.6043 7.89131 11.2247 8.33333 11.6667M8.33333 8.33333L11.6667 11.6667M8.33333 8.33333L5.83333 5.83333M11.6667 11.6667C12.1087 11.2247 12.3333 10.6043 12.3333 10C12.3333 9.39565 12.1087 8.77535 11.6667 8.33333M11.6667 11.6667L14.1667 14.1667M5.83333 5.83333C4.25 7.41667 2.5 9.58333 2.5 10C2.5 10.4167 4.25 12.5833 5.83333 14.1667M5.83333 5.83333L3.33333 3.33333M14.1667 14.1667C15.75 12.5833 17.5 10.4167 17.5 10C17.5 9.58333 15.75 7.41667 14.1667 5.83333M14.1667 14.1667L16.6667 16.6667" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <path d="M2.5 10C2.5 10 5.83333 5.83333 10 5.83333C14.1667 5.83333 17.5 10 17.5 10C17.5 10 14.1667 14.1667 10 14.1667C5.83333 14.1667 2.5 10 2.5 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              type="submit" 
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>

          <div className="social-login">
            <div className="divider">
              <span>Or continue with</span>
            </div>
            <button
              type="button"
              className="social-button google-button"
              onClick={handleGoogleLogin}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.6 10.2273C19.6 9.51818 19.5364 8.83636 19.4182 8.18182H10V12.05H15.3818C15.15 13.3 14.4455 14.3591 13.3864 15.0682V17.5773H16.6182C18.5091 15.8364 19.6 13.2727 19.6 10.2273Z" fill="#4285F4"/>
                <path d="M10 20C12.7 20 14.9636 19.1045 16.6182 17.5773L13.3864 15.0682C12.4909 15.6682 11.3455 16.0227 10 16.0227C7.39545 16.0227 5.19091 14.2636 4.40455 11.9H1.06364V14.4909C2.70909 17.7591 6.09091 20 10 20Z" fill="#34A853"/>
                <path d="M4.40455 11.9C4.20455 11.3 4.09091 10.6591 4.09091 10C4.09091 9.34091 4.20455 8.7 4.40455 8.1V5.50909H1.06364C0.386364 6.85909 0 8.38636 0 10C0 11.6136 0.386364 13.1409 1.06364 14.4909L4.40455 11.9Z" fill="#FBBC05"/>
                <path d="M10 3.97727C11.4682 3.97727 12.7864 4.48182 13.8227 5.47273L16.6909 2.60455C14.9591 0.990909 12.6955 0 10 0C6.09091 0 2.70909 2.24091 1.06364 5.50909L4.40455 8.1C5.19091 5.73636 7.39545 3.97727 10 3.97727Z" fill="#EA4335"/>
              </svg>
              <span>Google</span>
            </button>
          </div>

          <div className="signup-link">
            <span>Already have an account?</span>
            <button type="button" className="signup-text" onClick={onSwitchToLogin}>Sign in</button>
          </div>

          <div className="terms-text">
            By signing up, you agree to our{' '}
            <button type="button" className="terms-link" onClick={(e) => e.preventDefault()}>Terms of Service</button>
            {' '}and{' '}
            <button type="button" className="terms-link" onClick={(e) => e.preventDefault()}>Privacy Policy</button>
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="features-content">
          <div className="trust-indicator">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Trusted by 10,000+ QA Teams</span>
          </div>
          
          <h2 className="features-title">Enterprise-Grade Test Automation</h2>
          <p className="features-description">
            Streamline your testing workflow with powerful automation, intelligent scheduling, and comprehensive reporting.
          </p>

          <div className="features-list">
            <div className="feature-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.6667 5L7.5 14.1667L3.33333 10" stroke="#00BFA5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="feature-content">
                <h3>Record & Replay Tests</h3>
                <p>Create automated tests without writing code</p>
              </div>
            </div>

            <div className="feature-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.6667 5L7.5 14.1667L3.33333 10" stroke="#00BFA5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="feature-content">
                <h3>CI/CD Integration</h3>
                <p>Seamlessly integrate with Jenkins, GitHub Actions, and more</p>
              </div>
            </div>

            <div className="feature-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.6667 5L7.5 14.1667L3.33333 10" stroke="#00BFA5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="feature-content">
                <h3>Real-Time Analytics</h3>
                <p>Track test performance and identify flaky tests instantly</p>
              </div>
            </div>
          </div>

          <div className="user-rating">
            <div className="user-avatars">
              <div className="avatar"></div>
              <div className="avatar"></div>
              <div className="avatar"></div>
              <span className="avatar-count">+5K</span>
            </div>
            <div className="rating-info">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 1.66667L12.575 7.15833L18.3333 8.09167L14.1667 12.1583L15.15 17.975L10 15.2417L4.85 17.975L5.83333 12.1583L1.66667 8.09167L7.425 7.15833L10 1.66667Z" fill="#FBBF24" stroke="#FBBF24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ))}
              </div>
              <span className="rating-text">Rated 4.9/5 by our users</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;

